// One sign-in for everyone (admins, techs, blog editors). The role comes
// from the user's profile on the server and decides where they land.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchCurrentUser, signIn, signOut, signUp } from '../api/auth';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Shape the server user like the old Firebase user objects the pages read
// (uid, email, displayName).
const toCurrentUser = (u) => (u ? { uid: u.uid, email: u.email, name: u.name, displayName: u.name || '' } : null);

export const isStaffRole = (role) => role === 'admin' || role === 'tech';

// Where a user goes right after signing in. Staff check in first; the
// attendance page then continues to their dashboard.
export const homePathFor = (role, lang) => {
  if (role === 'admin') return `/${lang}/admin-dashboard`;
  if (role === 'tech') return `/${lang}/tech-dashboard`;
  return `/${lang}/`;
};

// Where to continue after login and check-in: the staff page the user was
// sent away from (e.g. a link to a report), otherwise their dashboard.
export const nextPathFor = (role, lang, next) => {
  const ok = typeof next === 'string' && /^\/(ar|en)\/[a-z]/.test(next) &&
    !/^\/(ar|en)\/(login|register|attendance)\b/.test(next);
  return ok ? next : homePathFor(role, lang);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apply = useCallback((u) => {
    setCurrentUser(toCurrentUser(u));
    setUserRole(u ? u.role : null);
    return u;
  }, []);

  useEffect(() => {
    fetchCurrentUser()
      .then(apply)
      .catch(() => apply(null))
      .finally(() => setLoading(false));
  }, [apply]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      return apply(await signIn(email, password));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [apply]);

  const register = useCallback(async (email, password, name) => {
    setError(null);
    try {
      return apply(await signUp(email, password, name));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [apply]);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      apply(null);
    }
  }, [apply]);

  const value = {
    currentUser,
    userRole,
    loading,
    error,
    login,
    register,
    logout,
    canAccessReports: () => isStaffRole(userRole),
    canModifyAllReports: () => userRole === 'admin',
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
